<template>
    <div class="step-content">
        <slot name="title">
            <StepTitle
                :active="active"
                :error="error"
                :complete="complete"
                :title="$t(`email.broadcast.editor.sectionTitle.sender`)"
            />
        </slot>

        <div class="main-content">
            <div v-show="active" class="input-fields">
                <ds-input-field
                    v-model="sender.name"
                    class="sender-name"
                    readonly
                    :label="$t('email.broadcast.steps.sender.name')"
                />

                <ds-multiselect
                    v-model="sender"
                    help-text
                    help-text-prop="email"
                    label-prop="name"
                    searchable
                    submitted
                    class="sender-multiselect"
                    allow-add
                    value-prop="label"
                    :force-invalid="hasValidationError"
                    :loading="isLoading"
                    :options="users"
                    :placeholder="$t('global.email')"
                    :add-text="$t('email.broadcast.steps.sender.customizeEmail')"
                    @add="handleAddCustomEmail"
                >
                    <template #error>
                        {{ $t('email.broadcast.steps.sender.error') }}
                    </template>
                </ds-multiselect>

                <DsCheckbox
                    v-model="customizeReplyToChecked"
                    :label="$t('email.broadcast.steps.replyTo.checkboxLabel')"
                    data-qa="customize-reply-to-checkbox"
                />

                <ds-multiselect
                    v-if="customizeReplyToChecked"
                    v-model="replyToAddress"
                    class="reply-to-multiselect"
                    :force-invalid="replyToError"
                    :options="users"
                    :placeholder="$t('email.broadcast.steps.replyTo.replyToLabel')"
                    :add-text="$t('email.broadcast.steps.replyTo.customizeEmail')"
                    help-text
                    help-text-prop="email"
                    value-prop="label"
                    label-prop="name"
                    :label="$t('email.broadcast.steps.sender.name')"
                    searchable
                    submitted
                    allow-add
                    data-qa="reply-to-dropdown"
                    @add="handleAddNewReplyToEmail"
                    @update:modelValue="validate"
                >
                    <template #error>
                        {{ $t('error') }}
                    </template>
                </ds-multiselect>

                <DkimVerificationAlert
                    v-show="dkimError || dkimWarning"
                    class="dkim-verification-alert"
                    :sender-list="senderList"
                    :sender="sender.value"
                    :contact-owner-identifier="$options.DEFAULT_FROM_VALUE"
                    :show-verified-domains-new-keys-only-enabled="showVerifiedDomainsNewKeysOnlyEnabled"
                    @validity="onSenderValidityChange"
                    @error-loading-domains="$emit('error', $options.STEP_ERRORS.ERROR_FETCHING_CUSTOM_DOMAINS)"
                />

                <ds-outline-button
                    class="save"
                    :disabled="disableSave"
                    variant="outline"
                    data-qa="sender-step-save-button"
                    @click.stop="save"
                >
                    {{ $t('global.save') }}
                </ds-outline-button>

                <ds-text-button
                    class="cancel"
                    variant="text"
                    @click="cancel"
                >
                    {{ $t('global.cancel') }}
                </ds-text-button>
            </div>

            <EmailBroadcastStepOverview v-if="!active" :sections="overviewSections" />
        </div>

        <component
            :is="customSenderModal"
            :is-open="isValidatedDomainsModalOpen"
            :domains="validatedDomains"
            :environment="environment"
            :merge-fields="mergeFields"
            :show-verified-domains-new-keys-only-enabled="showVerifiedDomainsNewKeysOnlyEnabled"
            :address="sender.value"
            data-qa="email-broadcast-validated-domains-modal"
            @custom-sender-selected="handleCustomEmailSelected"
            @close="handleValidatedDomainsModalClose"
        />

        <EmailAddCustomReplyToModal
            v-if="addCustomEmailReplyToModalOpen"
            :valid-domain-options="validatedDomains"
            is-open
            @close="addCustomEmailReplyToModalOpen = false"
            @save="handleCustomEmailReplyToAdd"
        />

        <slot name="action" :actionText="$t('email.broadcast.steps.sender.actionText')" />
    </div>
</template>

<script>
import EmailBroadcastStepOverview from './EmailBroadcastStepOverview.vue';
import StepTitle from '@/components/unlayer/UnlayerEmailStepTitle.vue';
import EmailBroadcastValidatedDomainsModal from '../EmailBroadcastValidatedDomainsModal';
import AddCustomEmailSenderModal from '../AddCustomEmailSenderModal.vue';
import DkimVerificationAlert from '@/components/shared/DkimVerificationAlert';
import {
    getCustomDomains,
    unlayerBroadcastsGetUsers,
} from '@/api/broadcasts/emailBroadcast';
import {
    activeSortedUsers,
    getEmailLabel,
    getEmailValue,
    getEmailName,
    getFromAddress,
} from '@/components/broadcast/utils/emailBroadcast.util';
import {
    DEFAULT_FROM_VALUE,
} from '@/components/broadcast/constants/emailBroadcast.constants';
import {
    getVerifiedDomainsKeysOrDmarc, getVerifiedDomainsNewKeys,
} from '@/components/settings/util/domains.util';
import { STEP_ERRORS } from '@/components/unlayer/constants/editor.constants';
import EmailAddCustomReplyToModal from '@/components/broadcast/email/EmailAddCustomReplyToModal';
import { isValidEmailAddress, MERGE_FIELD_REGEX } from '@/utils/email.util';
import cloneDeep from 'lodash.clonedeep';


const EMPTY_SENDER = {
    email: '',
    name: '',
};

export default {
    DEFAULT_FROM_VALUE,
    STEP_ERRORS,

    components: {
        EmailAddCustomReplyToModal,
        EmailBroadcastStepOverview,
        DkimVerificationAlert,
        StepTitle,
    },

    props: {
        active: Boolean,
        error: Boolean,
        complete: Boolean,
        savedFrom: String,
        savedReplyToAddress: String,
        mergeFields: Array,
        showErrors: Boolean,
        showVerifiedDomainsNewKeysOnlyEnabled: Boolean,
        wrapSenderDisplayNameWithQuotesEnabled: Boolean,
        mergeFieldsInFromAddressEnabled: Boolean,
        environment: String,
        isMaxClassic: Boolean,
    },

    data() {
        return {
            addCustomEmailReplyToModalOpen: false,
            customizeReplyToChecked: false,
            replyToError: false,
            hasValidationError: false,
            isLoading: true,
            isValidatedDomainsModalOpen: false,
            sender: EMPTY_SENDER,
            replyToAddress: {
                email: '',
                name: '',
                value: '',
            },
            users: [],
            validatedDomains: [],
            dkimError: false,
            dkimWarning: false,
            dkimErrorEmittedOnLoad: false,
            saveEmittedIfUserAndEmailDidNotMatch: false,
        };
    },

    created() {
        this.init();
    },

    watch: {
        savedFrom: {
            handler() {
                this.syncData();
            },
            immediate: true,
        },

        savedReplyToAddress: {
            handler() {
                this.syncData();
            },
            immediate: true,
        },

        showErrors(val) {
            if (val) this.validate();
        },

        isSenderDkimValid() {
            this.validate();
        },

        active(val) {
            if (!val) return;

            this.validate();
        },
    },

    computed: {
        customSenderModal() {
            return !this.isMaxClassic && this.mergeFieldsInFromAddressEnabled ? AddCustomEmailSenderModal : EmailBroadcastValidatedDomainsModal;
        },

        contactOwnerUser() {
            return {
                email: this.$t('email.broadcast.steps.sender.fromContact'),
                label: this.$t('email.broadcast.steps.sender.fromContact'),
                name: this.$t('email.broadcast.steps.sender.fromContact'),
                value: DEFAULT_FROM_VALUE,
            };
        },

        disableSave() {
            return this.dkimError;
        },

        overviewSections() {
            if (this.customizeReplyToChecked && this.replyToAddress.value.length > 0) {
                return [{
                    content: this.sender.name,
                    placeholder: this.$t('email.broadcast.steps.sender.placeholders.name'),
                    title: this.$t('email.broadcast.steps.sender.name'),
                }, {
                    content: this.sender.email,
                    placeholder: this.$t('email.broadcast.steps.sender.placeholders.email'),
                    title: this.$t('global.email'),
                }, {
                    content: `"${this.replyToAddress.name}" <${this.replyToAddress.email}>`,
                    placeholder: this.$t('email.broadcast.steps.sender.placeholders.email'),
                    title: this.$t('email.broadcast.steps.replyTo.replyToLabel'),
                }];
            }

            return [{
                content: this.sender.name,
                placeholder: this.$t('email.broadcast.steps.sender.placeholders.name'),
                title: this.$t('email.broadcast.steps.sender.name'),
            }, {
                content: this.sender.email,
                placeholder: this.$t('email.broadcast.steps.sender.placeholders.email'),
                title: this.$t('global.email'),
            }];
        },

        senderList() {
            return cloneDeep(this.users.map((user) => user.value));
        },

        isSenderDkimValid() {
            return !this.dkimError;
        },
    },

    methods: {
        async init() {
            this.isLoading = true;

            await Promise.allSettled([
                this.fetchValidatedCustomDomains(),
                this.fetchUsers(),
            ]);

            this.isLoading = false;

            this.syncData();
        },

        cancel() {
            this.hasValidationError = false;
            this.syncData();
            this.$emit('cancel');
        },

        async fetchValidatedCustomDomains() {
            try {
                const customDomains = await getCustomDomains();

                if (!this.showVerifiedDomainsNewKeysOnlyEnabled) {
                    this.validatedDomains = getVerifiedDomainsKeysOrDmarc(customDomains).map(
                        (domain) => {
                            return {
                                label: `@${domain}`,
                                value: `@${domain}`,
                            };
                        },
                    );

                    return;
                }

                this.validatedDomains = getVerifiedDomainsNewKeys(customDomains).map(
                    (domain) => {
                        return {
                            label: `@${domain}`,
                            value: `@${domain}`,
                        };
                    },
                );
            } catch (e) {
                this.$emit('error', STEP_ERRORS.ERROR_FETCHING_CUSTOM_DOMAINS);
            }
        },

        async fetchUsers() {
            try {
                const users = await unlayerBroadcastsGetUsers();

                this.users = [
                    cloneDeep(this.contactOwnerUser),
                    ...activeSortedUsers(users).map(({ id, fullName, email }) => ({
                        email,
                        id,
                        label: getEmailLabel({ email, fullName }),
                        name: fullName,
                        value: getEmailValue({ email, fullName }, this.wrapSenderDisplayNameWithQuotesEnabled),
                    })),
                ];
            } catch (e) {
                this.$emit('error', STEP_ERRORS.ERROR_FETCHING_USERS);
            }
        },

        handleAddCustomEmail() {
            this.isValidatedDomainsModalOpen = true;
        },

        handleValidatedDomainsModalClose() {
            this.isValidatedDomainsModalOpen = false;
        },

        save() {
            this.validate();

            if (this.hasValidationError) return;

            if (this.customizeReplyToChecked && this.replyToAddress.value.length !== 0) {
                this.$emit('save', {
                    replyToAddress: this.customizeReplyToChecked ? this.replyToAddress.value : '',
                    from: this.sender.value,
                });
            } else {
                this.$emit('save', {
                    from: this.sender.value,
                });
            }
        },

        syncData() {
            const from = this.savedFrom;
            const replyToAddress = this.savedReplyToAddress;

            if (!from) return;

            if (typeof replyToAddress === 'string' && replyToAddress.length !== 0 && replyToAddress !== DEFAULT_FROM_VALUE) {
                const clonedReplyToAddress = cloneDeep(replyToAddress);

                this.replyToAddress.value = clonedReplyToAddress;
                this.replyToAddress.email = getFromAddress(clonedReplyToAddress);
                this.replyToAddress.name = getEmailName(clonedReplyToAddress);
            } else {
                this.replyToAddress = cloneDeep(this.contactOwnerUser);
            }

            if (typeof replyToAddress === 'string' && replyToAddress.length !== 0) {
                this.customizeReplyToChecked = true;
            }

            if (from === DEFAULT_FROM_VALUE) {
                this.sender = cloneDeep(this.contactOwnerUser);
                this.hasValidationError = false;
            } else {
                if (this.isLoading) {
                    return;
                }

                try {
                    const email = getFromAddress(from).toLowerCase();
                    const name = getEmailName(from).toLowerCase();
                    let user;

                    user = cloneDeep(this.users.find((sender) => {
                        return sender.email.toLowerCase() === email && sender.name.toLowerCase() === name;
                    }));

                    const didUserNameAndEmailMatch = !!user;

                    if (!user) {
                        const isEmailFromValidDomain = this.validatedDomains.some(({ value }) => from.includes(value));
                        const isMergeField = MERGE_FIELD_REGEX.test(email);

                        if (isEmailFromValidDomain || isMergeField) {
                            user = {
                                email: getFromAddress(from),
                                name: getEmailName(from),
                                value: from,
                            };
                        }
                    }

                    const isSenderFromCustomDomain = !!user;

                    if (!user) {
                        user = cloneDeep(this.users.find((sender) => sender.email.toLowerCase() === email));
                    }

                    if (!user) {
                        user = cloneDeep(this.users.find((sender) => sender.name.toLowerCase() === name));
                    }

                    if (!user) {
                        throw new Error();
                    }

                    this.sender = user;
                    this.hasValidationError = false;

                    if (!isSenderFromCustomDomain && !didUserNameAndEmailMatch && !this.saveEmittedIfUserAndEmailDidNotMatch) {
                        this.save();
                        this.saveEmittedIfUserAndEmailDidNotMatch = true;
                    }
                } catch (e) {
                    this.hasValidationError = true;
                    this.sender = EMPTY_SENDER;
                    this.$emit('error', STEP_ERRORS.SENDER_NOT_FOUND);
                }
            }
        },

        handleCustomEmailSelected(customSender) {
            this.sender = {
                name: customSender.displayName,
                email: customSender.emailAddress,
                value: getEmailLabel({ email: customSender.emailAddress, fullName: customSender.displayName }),
            };
        },

        validate() {
            this.hasValidationError = !this.sender.value || !this.isSenderDkimValid;
            this.replyToError = isValidEmailAddress(this.replyToAddress.value);
        },

        onSenderValidityChange({ error, warning }) {
            if (MERGE_FIELD_REGEX.test(this.sender.email)) {
                this.dkimError = false;
                this.dkimWarning = false;
            } else {
                this.dkimError = error;
                this.dkimWarning = warning;
            }

            if (!this.dkimErrorEmittedOnLoad) {
                this.emitDkimErrorIfInvalid();
                this.dkimErrorEmittedOnLoad = true;
            }
        },

        handleAddNewReplyToEmail() {
            this.addCustomEmailReplyToModalOpen = true;
        },

        handleCustomEmailReplyToAdd(customEmailAddress) {
            this.replyToAddress = customEmailAddress;
        },

        emitDkimErrorIfInvalid() {
            if (!this.dkimError) {
                return;
            }

            this.$emit('error', STEP_ERRORS.SENDER_DKIM_ERROR);
        },
    },
};
</script>

<style lang="scss" scoped>
@import './steps';

.step-content {
    --input-padding-top: 0;
    --input-padding-right: 15px;
}

.sender-name {
    --input-padding: 0;
    --input-padding-top: 0;
    --input-padding-right: 0;
    --input-padding-bottom: 0;
    --input-padding-left: 15px;
}

.dkim-verification-alert {
    margin-bottom: $spacing-125;
    width: 100%;
}

.sender-multiselect {
    --input-margin-bottom: #{$spacing-125};
    --input-padding-bottom: 0;
}

.reply-to-multiselect {
    margin-top: #{$spacing-125};
    --input-padding-bottom: 0;
}
</style>
